import React from "react";
import { ICreateSalesData, ICustomer, ItemInput } from "../../@types";
import {
  createDraftData,
  createInvoiceData,
} from "../../modules/invoice/invoice";
import { handleErrors } from "../../utils";
import { useNavigate } from "react-router-dom";
import { getInvoice, updateToPending } from "../../backend-services";
import { useCustomToast } from "../../utils/toastUtils";

interface Props {
  isRetail: boolean;
  setShowSidebar: (value: React.SetStateAction<boolean>) => void;
  setIsisDraftLoading: (value: React.SetStateAction<boolean>) => void;
  customer: ICustomer | null;
  formData: ICreateSalesData;
  setShowSubscriptionModal: (value: React.SetStateAction<boolean>) => void;
  setErrorMessage: (value: React.SetStateAction<string>) => void;
  setIsLoading: (value: React.SetStateAction<boolean>) => void;
  setSubscriptionErrorMessage: (value: React.SetStateAction<string>) => void;
  invoiceNo: string | undefined;
  setCreatedInvoiceId: (value: React.SetStateAction<string>) => void;
  setCreatedEmail: (value: React.SetStateAction<string>) => void;
  setShowSuccessModal: (value: React.SetStateAction<boolean>) => void;
  setBannerLinkText: (value: React.SetStateAction<string>) => void;
  setError: (value: React.SetStateAction<string>) => void;
  setBannerMessage: (value: React.SetStateAction<string>) => void;
  setIsBannerVisible: (value: React.SetStateAction<boolean>) => void;
  setLoading: (value: React.SetStateAction<boolean>) => void;
  setFormData: (value: React.SetStateAction<ICreateSalesData>) => void;
  setCustomer: (value: React.SetStateAction<ICustomer | null>) => void;
  setItems: (value: React.SetStateAction<ItemInput[]>) => void;
}

export const useInvoiceActions = ({
  isRetail,
  setShowSidebar,
  setIsisDraftLoading,
  customer,
  formData,
  setShowSubscriptionModal,
  setErrorMessage,
  setIsLoading,
  setSubscriptionErrorMessage,
  invoiceNo,
  setBannerLinkText,
  setCreatedEmail,
  setCreatedInvoiceId,
  setError,
  setShowSuccessModal,
  setBannerMessage,
  setIsBannerVisible,
  setCustomer,
  setFormData,
  setItems,
  setLoading,
}: Props) => {
  const { showToast, showToastWithProps } = useCustomToast();
  const navigate = useNavigate();
  const normalizePrice = (price: any): number => {
    if (price === null || price === undefined) return NaN;
    if (typeof price === "object") {
      const objNum = Number(
        price?.amount ?? price?.value ?? price?.price ?? price?.[0]?.amount,
      );
      return objNum;
    }
    return typeof price === "string" ? parseFloat(price) : Number(price);
  };

  const hasInvalidServicePrices = (services: any[]): string | null => {
    for (let i = 0; i < services.length; i++) {
      const s = services[i];
      const n = normalizePrice(s?.price);
      if (!Number.isFinite(n) || n <= 0) {
        return `Service line ${i + 1}: price must be greater than 0`;
      }
    }
    return null;
  };
  function wrapVariationsAsArray(data: ICreateSalesData) {
    return {
      ...data,
      inventories: data.inventories.map((inventory) => ({
        ...inventory,
        variations: Array.isArray(inventory.variations)
          ? inventory.variations
          : inventory.variations
            ? [inventory.variations]
            : [],
      })),
    };
  }
  const processCreateDraftInvoice = async (e?: any) => {
    const exchangeRate = formData.exchangeRate || 1;
    setIsisDraftLoading(true);
    setShowSidebar(false);

    // Validate service prices > 0 for service invoices
    if (!isRetail && (formData.services?.length || 0) > 0) {
      const err = hasInvalidServicePrices(formData.services as any[]);
      if (err) {
        setIsisDraftLoading(false);
        setShowSidebar(true);
        setErrorMessage(err);
        showToast(err, "error");
        const errorEl = document.getElementById("error-message");
        errorEl?.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }

    const invoiceData: ICreateSalesData = {
      customerId: customer?.id,
      description: formData.description,
      invoiceDueAt: formData?.invoiceDueAt
        ? new Date(formData?.invoiceDueAt)?.toISOString()
        : undefined,
      issueDate: formData?.issueDate
        ? new Date(formData?.issueDate)?.toISOString()
        : undefined,
      additionalInformation: formData.additionalInformation,
      discount: Number(formData.discount ? formData.discount / 100 : 0),
      tax: Number(formData.tax ? formData.tax / 100 : 0),
      hasShipping: formData.hasShipping || !!formData.shippingCharge,
      shippingCharge: Number(formData.shippingCharge || 0),
      shippingDesc: formData.shippingDesc || "",
      isReoccurring: formData.isReoccurring,
      status: "draft",
      currency: formData.currency,
      exchangeRate: formData.exchangeRate,
      invoiceType: isRetail ? "inventory" : "service",
      // @ts-ignore
      reminderSettings: formData.reminderSettings,
      inventories: isRetail
        ? formData.inventories.length > 0
          ? formData.inventories.map(
              (i: {
                id: string;
                name: string;
                rate: number;
                quantity: number;
                variations?: any;
              }) => ({
                id: i.id,
                name: i.name,
                amount: 0,
                quantity: Number(i.quantity),
                rate: Number(i.rate),
                variations:
                  i.variations && i.variations.length > 0
                    ? (i.variations.map((variation: any) => ({
                        ...variation,
                        rate: +variation.price / exchangeRate,
                        price: +variation.price / exchangeRate,
                      })) ?? i.variations)
                    : undefined,
              }),
            )
          : []
        : [],
      services: !isRetail
        ? formData.services.length
          ? formData.services.map(
              (s: { id: ""; name: ""; duration: ""; price: "" }) => ({
                id: s.id,
                name: s.name,
                duration: s.duration,
                price: Number(s.price),
              }),
            )
          : undefined
        : undefined,
      ...(formData.isReoccurring && {
        reoccurringGroupName: formData.reoccurringGroupName,
        isReoccurringStartDate: new Date().toISOString(),
        isReoccurringEndDate: new Date(
          formData.isReoccurringEndDate || "",
        )?.toISOString(),
      }),
    };

    try {
      const response = await createDraftData(invoiceData, customer, isRetail);
      setIsisDraftLoading(false);
      showToast("Draft saved successfully!", "success");
      return response;
    } catch (error: any) {
      handleErrors(error, setErrorMessage);
      setIsisDraftLoading(false);
      if (error.response) {
        const errorMessage = error.response.data;
        setShowSubscriptionModal(true);
        setSubscriptionErrorMessage(errorMessage);
      }
      const errorEl = document.getElementById("error-message");
      errorEl?.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
      document
        .getElementById("top-of-page")
        ?.scrollIntoView({ behavior: "smooth" });
      setIsLoading(false);
      setErrorMessage("An error occurred.");
    }
  };

  const processCreateInvoice = async (
    sendEmail: boolean,
    markedAsPaid: boolean,
    shouldSendWhatsApp: boolean,
    shouldSendSMS: boolean,
    redirectToProject: boolean,
  ) => {
    const data = wrapVariationsAsArray(formData);
    const exchangeRate = formData.exchangeRate || 1;

    try {
      setIsLoading(true);

      // Validate service prices > 0 for service invoices
      if (!isRetail && (data.services?.length || 0) > 0) {
        const err = hasInvalidServicePrices(data.services as any[]);
        if (err) {
          setIsLoading(false);
          setErrorMessage(err);
          showToast(err, "error");
          const errorEl = document.getElementById("error-message");
          errorEl?.scrollIntoView({ behavior: "smooth", block: "start" });
          return false;
        }
      }

      // setShowCreateSidebar(false);
      const {
        invoiceDueAt,
        issueDate,
        inventories,
        services,
        discount,
        tax,
        customerId,
        isReoccurring,
        projectId,
      } = data;
      const validInvoiceDueAt = invoiceDueAt ? new Date(invoiceDueAt) : null;
      const validIssueDate = issueDate ? new Date(issueDate) : null;

      if (!validInvoiceDueAt || isNaN(validInvoiceDueAt.getTime())) {
        throw new Error("Invalid invoice due date");
      }

      if (!validIssueDate || isNaN(validIssueDate.getTime())) {
        throw new Error("Invalid issue date");
      }

      const invoiceData: ICreateSalesData = {
        customerId: customerId,
        shouldSendEmail: sendEmail,
        isPaymentCompleted: markedAsPaid,
        shouldSendWhatsApp: shouldSendWhatsApp,
        shouldSendSMS: shouldSendSMS,
        isReoccurring: isReoccurring,
        shippingCharge: Number(formData.shippingCharge || 0),
        shippingDesc: formData.shippingDesc || "",
        invoiceDueAt: validInvoiceDueAt.toISOString(),
        issueDate: validIssueDate.toISOString(),
        inventories: isRetail
          ? inventories.length
            ? inventories.map(
                (i: {
                  id: string;
                  name: string;
                  rate: number;
                  quantity: number;
                  variations?: any;
                }) => {
                  return {
                    id: i.id,
                    name: i.name,
                    amount: 0,
                    quantity:
                      i.variations && i.variations.length > 0
                        ? 0
                        : Number(i.quantity),
                    rate:
                      i.variations && i.variations.length > 0
                        ? 0
                        : Number(i.rate) / exchangeRate,
                    variations:
                      i.variations && i.variations.length > 0
                        ? i.variations.map((variation: any) => ({
                            ...variation,
                            quantity:
                              i.quantity == 0 ? variation.quantity : i.quantity,
                            rate:
                              +variation.rate / exchangeRate ||
                              +variation.price / exchangeRate,
                            price:
                              +variation.rate / exchangeRate ||
                              +variation.price / exchangeRate,
                          }))
                        : undefined,
                  };
                },
              )
            : []
          : [],
        services: !isRetail
          ? services.length
            ? services.map(
                (s: { id: ""; name: ""; duration: ""; price: "" }) => ({
                  id: s.id,
                  name: s.name,
                  duration: s.duration.toString(),
                  price:
                    // @ts-ignore
                    s.price && typeof s.price == "object" && s.price?.length > 0
                      ? // @ts-ignore
                        +s.price[0]?.amount / exchangeRate
                      : Number(s.price) / exchangeRate,
                }),
              )
            : []
          : [],
        reminderSettings: formData.reminderSettings,
        discount: Number(discount ? discount : 0),
        tax: Number(tax ? tax : 0),
        isReoccurringEndDate: formData.isReoccurringEndDate || "",
        reoccurringGroupName: formData.reoccurringGroupName || "",
        status: formData.status || "pending",
        description: formData.description || "",
        additionalInformation: formData.additionalInformation || "",
        currency: formData.currency || "",
        exchangeRate: formData.exchangeRate,
        paymentMethod: formData.paymentMethod || "",
        invoiceType: isRetail ? "inventory" : "service",
        projectId: formData.projectId || "",
      };
      const promises = [];

      if (customer) {
        if (invoiceNo) {
          const taxValue = formData.tax ? Number(formData.tax) / 100 : 0;
          const formattedTax = taxValue.toFixed(2);
          const updatedInvoiceData = {
            status: "pending",
            invoiceDueAt: validInvoiceDueAt.toISOString(),
            shippingCharge: Number(formData.shippingCharge || 0), // Add this
            shippingDesc: formData.shippingDesc || "",
            issueDate: validIssueDate.toISOString(),
            invoiceType: "freeform",
            inventories: isRetail
              ? inventories.length
                ? inventories.map(
                    (i: {
                      id: string;
                      name: string;
                      rate: number;
                      quantity: number;
                      variations?: any;
                    }) => {
                      return {
                        id: i.id,
                        name: i.name,
                        amount: 0,
                        quantity:
                          i.variations && i.variations.length > 0
                            ? 0
                            : Number(i.quantity),
                        rate:
                          i.variations && i.variations.length > 0
                            ? 0
                            : Number(i.rate) / exchangeRate,
                        variations:
                          i.variations && i.variations.length > 0
                            ? i.variations.map((variation: any) => ({
                                ...variation,
                                quantity:
                                  i.quantity == 0
                                    ? variation.quantity
                                    : i.quantity,
                                rate:
                                  +variation.rate ||
                                  +variation.price / exchangeRate,
                                price:
                                  +variation.rate ||
                                  +variation.price / exchangeRate,
                              }))
                            : undefined,
                      };
                    },
                  )
                : []
              : [],
            services: !isRetail
              ? services.length
                ? services.map(
                    (s: { id: ""; name: ""; duration: ""; price: "" }) => ({
                      id: s.id,
                      name: s.name,
                      duration: s.duration,
                      price:
                        // @ts-ignore
                        s.price &&
                        typeof s.price == "object" &&
                        // @ts-ignore
                        s.price?.length > 0
                          ? // @ts-ignore
                            +s.price[0]?.amount / exchangeRate
                          : Number(s.price) / exchangeRate,
                    }),
                  )
                : []
              : [],
            reminderSettings: formData.reminderSettings,
            discount: Number(formData.discount ? formData.discount / 100 : 0),
            tax: Number(formattedTax),
            projectId: formData.projectId || "",
          };

          promises.push(
            updateToPending({ id: invoiceNo, data: updatedInvoiceData }),
          );
        } else {
          promises.push(createInvoiceData(invoiceData, customer, isRetail));
        }
      }
      const results = await Promise.all(promises);
      const createdUpdatedInvoice = results[0];

      const createdUpdatedInvoiceId = createdUpdatedInvoice?.id;
      const createdEmail = customer?.email || "";

      const handleViewInvoice = () => {
        if (createdUpdatedInvoiceId) {
          const viewInvoiceRoute = `/invoices/${createdUpdatedInvoiceId}/view`;
          navigate(viewInvoiceRoute);
        }
      };

      setCreatedInvoiceId(createdUpdatedInvoiceId);
      setCreatedEmail(createdEmail);
      if (redirectToProject) {
        showToast("Invoice added to the project.", "success");
        navigate(-1);
      } else {
        setShowSuccessModal(true);
        setBannerMessage("New Invoice Created");
        setBannerLinkText("View Invoice");
      }
      setIsBannerVisible(true);
      return true;
    } catch (error: any) {
      handleErrors(error, setErrorMessage);
      if (invoiceNo) {
        if (error.response) {
          const errorMessage = error.response.data;
          setError(errorMessage);
          setShowSubscriptionModal(true);
          setSubscriptionErrorMessage(errorMessage);
        } else {
          setErrorMessage("An error occurred while processing the invoice.");
          showToast("An error occurred while processing the invoice.", "error");
        }
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDraftInvoice = async (invoiceNo: string) => {
    if (!invoiceNo) return;

    setLoading(true);

    try {
      const response = await getInvoice(invoiceNo);
      const draftData = response;
      // Ensure we only process the invoice if it is a draft
      if (draftData && draftData.status === "draft") {
        setFormData((prevData) => ({
          ...prevData,
          customerId: draftData.customer?.id || "",
          invoiceDueAt: draftData.invoiceDueAt
            ? new Date(draftData.invoiceDueAt).toISOString()
            : "",
          issueDate: draftData.issueDate
            ? new Date(draftData.issueDate).toISOString()
            : "",
          total: draftData.total || 0,
          shippingCharge: draftData.shippingCharge || 0, // Add this
          shippingDesc: draftData.shippingDesc || "",
          currency: draftData.currency || "NGN",
          description: draftData.description || "",
          additionalInformation: draftData.additionalInformation || "",
          exchangeRate: draftData.exchangeRate || 1,
          invoiceType: draftData.invoiceType || "inventory",
          isReoccurring: draftData.isReoccurring || false,
          status: draftData.status || "draft",
        }));

        setCustomer({
          id: draftData.customer?.id || "",
          firstName: draftData.customer?.firstName || "",
          lastName: draftData.customer?.lastName || "",
          phone: draftData.customer?.phone || "",
          email: draftData.customer?.email || "",
        });

        // Handle inventory or service items
        if (draftData.invoiceType === "inventory") {
          setItems(
            draftData.inventories?.map(
              (i: {
                id: any;
                name: any;
                amount: any;
                quantity: any;
                rate: any;
                variations: any;
              }) => ({
                id: i.id,
                name: i.name,
                amount: i.amount || 0,
                quantity: Number(i.quantity) || 0,
                rate: Number(i.rate) || 0,
                variations: i.variations || [],
              }),
            ) || [],
          );
        } else if (draftData.invoiceType === "service") {
          setItems(
            draftData.services?.map(
              (service: {
                id: any;
                name: any;
                price: string | any[];
                duration: any;
              }) => ({
                id: service.id,
                name: service.name,
                price:
                  service.price?.length > 0
                    ? parseFloat(service.price[0].amount)
                    : 0,
                duration: service.duration,
              }),
            ) || [],
          );
        }
      } else {
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };
  return { processCreateInvoice, processCreateDraftInvoice, fetchDraftInvoice };
};
